/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * One editable goal (draft only). Autosaves (debounced) as the user edits — no
 * Save button. A tiered payout exposes an inline tier editor.
 */
import { useEffect, useRef, useState } from "react";
import { saveGoal, deleteGoal } from "../utils/incentiveService.js";
import { METRIC_TYPES, METRIC_DEFAULT_UNIT, METRIC_AUTO_HINT, PAYOUT_TYPES } from "../utils/constants.js";
import { hasAutomaticSource } from "../utils/metricResolver.js";
import ObjectMultiSelect from "./ObjectMultiSelect.jsx";

function blankGoal() {
  return {
    metric_type: "new_pods", description: "", custom_text: "",
    target_value: "", unit_label: "",
    payout_type: "flat", payout_amount: "", payout_tiers: [], payout_custom: "",
    target_accounts: [], target_products: [],
  };
}

/**
 * @param {object[]} accountOptions  doors this distributor sells, [{id,label,sub,snapshot}]
 * @param {object[]} productOptions  the tenant's Product SKUs, [{id,label,sub,snapshot}]
 */
export default function GoalRow({
  programId, goal, index, onSaved, onRemoved, disabled,
  accountOptions = [], productOptions = [], accountsNote = "",
}) {
  const [form, setForm] = useState(() => ({ ...blankGoal(), ...goal }));
  const [saveState, setSaveState] = useState(goal?.id ? "saved" : "idle");
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState(null);

  // id lives in a ref (not form) so persisting it doesn't retrigger autosave.
  const idRef = useRef(goal?.id || null);
  const formRef = useRef(form);
  formRef.current = form;
  const savingRef = useRef(false);
  const pendingRef = useRef(false);
  const pendingDeleteRef = useRef(false); // removed while an insert was in flight
  const firstRun = useRef(true);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  function changeMetric(mt) {
    set({ metric_type: mt, unit_label: form.unit_label || METRIC_DEFAULT_UNIT[mt] || "" });
  }
  function setTier(i, patch) {
    const tiers = form.payout_tiers.map((t, j) => (j === i ? { ...t, ...patch } : t));
    set({ payout_tiers: tiers });
  }
  function addTier() { set({ payout_tiers: [...form.payout_tiers, { min_value: "", amount: "" }] }); }
  function removeTier(i) { set({ payout_tiers: form.payout_tiers.filter((_, j) => j !== i) }); }

  // Serialized save: insert on first write (no id), update after. If edits land
  // mid-save, re-run with the latest values so nothing is lost.
  async function flush() {
    if (savingRef.current) { pendingRef.current = true; return; }
    savingRef.current = true;
    try {
      do {
        pendingRef.current = false;
        if (mountedRef.current) setSaveState("saving");
        idRef.current = await saveGoal(programId, { ...formRef.current, id: idRef.current || undefined });
      } while (pendingRef.current);
      // Removed while this insert was in flight — clean up the just-created row
      // so it isn't orphaned, and don't report "saved".
      if (pendingDeleteRef.current && idRef.current) {
        try { await deleteGoal(idRef.current); } catch { /* best effort */ }
        return;
      }
      if (mountedRef.current) { setSaveState("saved"); setErr(null); }
      onSaved?.();
    } catch (e) { if (mountedRef.current) { setErr(e.message); setSaveState("error"); } }
    finally { savingRef.current = false; }
  }

  // Autosave (debounced). New goals persist on their FIRST edit (not on mount),
  // so an untouched "Add goal" never writes a blank placeholder; existing goals
  // are not re-saved on mount.
  useEffect(() => {
    if (disabled) return;
    if (firstRun.current) { firstRun.current = false; return; }
    const t = setTimeout(() => { void flush(); }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, disabled]);

  async function remove() {
    setRemoving(true); setErr(null);
    // If an insert is mid-flight (no id yet), defer the delete to flush so the
    // just-created row isn't orphaned. An unsaved (never-edited) row just drops.
    if (savingRef.current && !idRef.current) { pendingDeleteRef.current = true; onRemoved?.(); return; }
    try {
      if (idRef.current) await deleteGoal(idRef.current);
      onRemoved?.();
    } catch (e) { setErr(e.message); setRemoving(false); }
  }

  const skuScoped = Array.isArray(form.target_products) && form.target_products.length > 0;
  const auto = hasAutomaticSource(form);

  return (
    <div className="it-goal">
      <div className="it-goal-head">
        <strong>Goal {index + 1}</strong>
        <button className="it-link-danger" onClick={remove} disabled={removing || disabled}>Remove</button>
      </div>

      <div className="it-goal-grid">
        <label className="it-span2">
          {form.metric_type === "custom" ? "Goal title" : "Description"}
          <input type="text" value={form.description || ""} placeholder="e.g. New points of distribution in Illinois"
                 onChange={(e) => set({ description: e.target.value })} disabled={disabled} />
        </label>

        <label className={form.metric_type === "custom" ? "it-span2" : ""}>
          Metric
          <select value={form.metric_type} onChange={(e) => changeMetric(e.target.value)} disabled={disabled}>
            {METRIC_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>

        {form.metric_type === "custom" ? (
          <label className="it-span2">
            Goal details <span className="muted">(free text — anything that doesn't fit the form)</span>
            <textarea rows={3} value={form.custom_text || ""} placeholder="Describe the goal in full…"
                      onChange={(e) => set({ custom_text: e.target.value })} disabled={disabled} />
          </label>
        ) : (
          <>
            <label>
              Target
              <input type="number" inputMode="decimal" value={form.target_value ?? ""} placeholder="0"
                     onChange={(e) => set({ target_value: e.target.value })} disabled={disabled} />
            </label>
            <label>
              Unit label
              <input type="text" value={form.unit_label || ""} placeholder="pods, cases…"
                     onChange={(e) => set({ unit_label: e.target.value })} disabled={disabled} />
            </label>
          </>
        )}

        <label>
          Payout type
          <select value={form.payout_type} onChange={(e) => set({ payout_type: e.target.value })} disabled={disabled}>
            {PAYOUT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>

        {form.payout_type === "custom" ? (
          <label className="it-span2">
            Payout details <span className="muted">(free text)</span>
            <textarea rows={2} value={form.payout_custom || ""} placeholder="Describe how the payout works…"
                      onChange={(e) => set({ payout_custom: e.target.value })} disabled={disabled} />
          </label>
        ) : form.payout_type !== "tiered" ? (
          <label>
            {form.payout_type === "per_unit" ? "Amount per unit ($)" : "Payout amount ($)"}
            <input type="number" inputMode="decimal" value={form.payout_amount ?? ""} placeholder="0"
                   onChange={(e) => set({ payout_amount: e.target.value })} disabled={disabled} />
          </label>
        ) : (
          <div className="it-span2">
            <label style={{ marginBottom: 8 }}>Tiers (reach the threshold → earn the amount)</label>
            <div className="it-tiers">
              {form.payout_tiers.length === 0 && <p className="muted" style={{ fontSize: 13, margin: 0 }}>No tiers yet.</p>}
              {form.payout_tiers.map((t, i) => (
                <div className="it-tier-row" key={i}>
                  <input type="number" inputMode="decimal" placeholder="At least…" value={t.min_value ?? ""}
                         onChange={(e) => setTier(i, { min_value: e.target.value })} disabled={disabled} />
                  <input type="number" inputMode="decimal" placeholder="Pays ($)" value={t.amount ?? ""}
                         onChange={(e) => setTier(i, { amount: e.target.value })} disabled={disabled} />
                  <button className="it-link-danger" onClick={() => removeTier(i)} disabled={disabled}>✕</button>
                </div>
              ))}
              <button className="secondary" onClick={addTier} disabled={disabled} style={{ alignSelf: "flex-start" }}>+ Add tier</button>
            </div>
          </div>
        )}

        {/* ── Scope (item 233) ───────────────────────────────────────────────
            target_accounts existed as a column with no UI, and there was no SKU
            reference anywhere in the module, while the product said goals are
            set "against the accounts already in your profile" and that SKUs
            flow straight in. Both are set here. Leaving either empty means
            "everything", which is the common case — so neither is required. */}
        <div className="it-field it-span2">
          <span>Accounts <span className="muted">(optional — leave empty for every account this distributor sells)</span></span>
          <ObjectMultiSelect
            options={accountOptions}
            value={Array.isArray(form.target_accounts) ? form.target_accounts : []}
            onChange={(next) => set({ target_accounts: next })}
            placeholder="All accounts"
            emptyMessage={accountsNote || "No accounts found for this distributor yet"}
            disabled={disabled}
          />
          {accountsNote && <span className="muted" style={{ fontSize: 12 }}>{accountsNote}</span>}
        </div>

        <div className="it-field it-span2">
          <span>SKUs <span className="muted">(optional — leave empty for all products)</span></span>
          <ObjectMultiSelect
            options={productOptions}
            value={Array.isArray(form.target_products) ? form.target_products : []}
            onChange={(next) => set({ target_products: next })}
            placeholder="All SKUs"
            emptyMessage="No Product SKUs set up yet — add them in System Settings"
            disabled={disabled}
          />
        </div>

        {/* Where this goal's actual will come from — stated at build time, so a
            rep knows before they send whether month-end is automatic. */}
        <p className={`it-source ${auto ? "auto" : ""} it-span2`}>
          {auto
            ? <>Scored automatically. {METRIC_AUTO_HINT[form.metric_type]}</>
            : skuScoped
              ? <>Scored by hand: this goal is narrowed to specific SKUs, and depletion reports roll up per account rather than per SKU.</>
              : <>Scored by hand — {form.metric_type === "custom" ? "a custom goal has no number to sum" : "this metric has no system source"}.</>}
        </p>
      </div>

      {err && <p className="error" style={{ fontSize: 13 }}>{err}</p>}
      {!disabled && (
        <p className="muted" style={{ fontSize: 12, margin: "10px 0 0" }}>
          {saveState === "saving" ? "Saving…"
            : saveState === "error" ? "Couldn't save — edit a field to retry."
            : saveState === "idle" ? "Saves automatically as you edit."
            : "Saved automatically."}
        </p>
      )}
    </div>
  );
}

export { blankGoal };
